/**
 * Story 057 — Asset model Autocomplete with grouped options.
 *
 * Christian's Q5 lock (2026-05-13): the Asset model dropdown sources
 * BOTH the tenant's permitted models (`scope: 'tenant'`) AND the rest
 * of the global catalog (`scope: 'global'`). The FE groups in the
 * Autocomplete dropdown:
 *  - "Frequently used" group header at the top (tenant-permitted)
 *  - "All models" group header beneath (full catalog, deduped)
 *
 * Used by both Add and Edit modals. Disabled until an Asset type is
 * picked.
 */
import { Autocomplete, TextField } from "@mui/material";
import type { ReactElement } from "react";
import { useReadOnlyContext } from "@aeriosiq/ui";
import type { AssetModelSummary } from "../queries/index.js";

export interface AssetModelAutocompleteProps {
  /** The currently selected asset model UUID (or null). */
  value: string | null;
  /** All candidate models (already filtered by selected asset type). */
  options: readonly AssetModelSummary[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  label?: string;
  /**
   * Inline error message shown beneath the field. Used by the Add /
   * Edit Turbine modals when the BE returns
   * `400 { detail: 'model_type_mismatch' }` — surfacing the reason in
   * place is more actionable than a generic toast (R58-W-03 AC2).
   */
  errorText?: string | null;
}

export function AssetModelAutocomplete({
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  loading = false,
  label = "Asset model",
  errorText = null,
}: AssetModelAutocompleteProps): ReactElement {
  const selected = options.find((o) => o.id === value) ?? null;
  // SB2-CC-03 — forward read-only state from `<ReadOnlyModal>` context.
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<AssetModelSummary>
      options={[...options]}
      value={selected}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.name}
      // Group by scope. "tenant" → "Frequently used"; "global" → "All
      // models". Inside each group the items keep their alphabetical
      // order (the BE sorts by scope then name).
      groupBy={(o) => (o.scope === "tenant" ? "Frequently used" : "All models")}
      onChange={(_e, next) => {
        onChange(next?.id ?? null);
      }}
      renderInput={(params) => {
        // exactOptionalPropertyTypes friction — same dance as Sites'
        // CountryAutocomplete and turbine-models' Manufacturer field.
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label={label}
            required={required}
            error={typeof errorText === "string" && errorText.length > 0}
            {...(typeof errorText === "string" && errorText.length > 0
              ? { helperText: errorText }
              : {})}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": label,
                ...(required ? { "aria-required": true } : {}),
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
